import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Search as SearchIcon, Users, FileCheck2, Download } from "lucide-react";
import { AdminTabs } from "@/components/AdminTabs";
import { Pagination } from "@/components/Pagination";
import { FilterPills } from "@/components/FilterPills";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { downloadCSV } from "@/lib/csv";

const STATUS_OPTIONS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "suspended", label: "Suspended" },
  { key: "consented", label: "Notify consent" },
];

export default function AdminCustomers() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [range, setRange] = useState({ start: "", end: "" });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const navigate = useNavigate();

  const load = async (query = "", p = 1, st = status, rg = range) => {
    setLoading(true);
    try {
      const params = { page: p, page_size: pageSize };
      if (query) params.q = query;
      if (st && st !== "all") params.status = st;
      if (rg.start) params.start = rg.start;
      if (rg.end) params.end = rg.end;
      const { data } = await api.get("/admin/customers", { params });
      setItems(data.items);
      setTotal(data.total);
      setPage(data.page);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(q.trim(), 1, status, range); /* eslint-disable-next-line */ }, [status]);

  const applyRange = (start, end) => { const rg = { start, end }; setRange(rg); load(q.trim(), 1, status, rg); };

  const exportCSV = async () => {
    setExporting(true);
    try {
      const params = { export: true };
      if (q.trim()) params.q = q.trim();
      if (status !== "all") params.status = status;
      if (range.start) params.start = range.start;
      if (range.end) params.end = range.end;
      const { data } = await api.get("/admin/customers", { params });
      const rows = (data.items || []).map((u) => ({
        name: u.name || "",
        username: u.username || "",
        email: u.email || "",
        phone: u.phone || "",
        notify_consent: u.notify_consent ? "Yes" : "No",
        agreements: u.agreements_count || 0,
        suspended: u.suspended ? "Yes" : "No",
      }));
      if (!rows.length) { toast.error("Nothing to export"); return; }
      downloadCSV(`customers-${status}.csv`, rows);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="container-x py-12" data-testid="admin-customers">
      <AdminTabs />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold flex items-center gap-2">
            <Users className="w-6 h-6 text-[#2D6A4F]" /> Customers ({total})
          </h1>
          <p className="text-[#5C6B64] mt-2">Manage registered customer accounts, view their requests and consent records.</p>
        </div>
        <button
          data-testid="admin-customers-export-csv"
          onClick={exportCSV}
          disabled={exporting || (!loading && items.length === 0)}
          className="btn-secondary inline-flex items-center gap-2 text-sm py-2 px-3 disabled:opacity-50"
        >
          <Download className="w-4 h-4" /> {exporting ? "Exporting…" : "Export CSV"}
        </button>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); load(q.trim(), 1, status); }} className="mt-6 flex items-center gap-2 max-w-md">
        <input data-testid="admin-customers-search" className="input-soft" placeholder="Search name, username, email, phone…" value={q} onChange={(e) => setQ(e.target.value)} />
        <button className="btn-primary py-2.5 px-4 inline-flex items-center gap-2" data-testid="admin-customers-search-btn"><SearchIcon className="w-4 h-4" /> Search</button>
      </form>

      <div className="mt-4 flex flex-col gap-3">
        <FilterPills options={STATUS_OPTIONS} value={status} onChange={setStatus} testidPrefix="admin-customers-filter" />
        <DateRangeFilter onChange={applyRange} />
      </div>

      {loading ? (
        <div className="mt-10 text-[#5C6B64]">Loading…</div>
      ) : items.length === 0 ? (
        <p className="mt-8 text-sm text-[#8A9A86]">No registered customers found.</p>
      ) : (
        <div className="mt-8 overflow-x-auto rounded-xl border border-[#1E3F33]/10">
          <table className="w-full text-sm" data-testid="admin-customers-table">
            <thead className="bg-[#F0EFEA] text-left text-[#1E3F33]"><tr>
              <th className="px-4 py-2.5 font-medium">Name</th>
              <th className="px-4 py-2.5 font-medium">Contact</th>
              <th className="px-4 py-2.5 font-medium">Consent</th>
              <th className="px-4 py-2.5 font-medium"></th>
            </tr></thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-t border-[#1E3F33]/8" data-testid={`admin-customer-row-${u.id}`}>
                  <td className="px-4 py-2.5"><div className="font-medium text-[#1E3F33]">{u.name || u.username} {u.suspended && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#E63946]/15 text-[#E63946] align-middle">Suspended</span>}</div><div className="text-xs text-[#8A9A86]">@{u.username}</div></td>
                  <td className="px-4 py-2.5 text-[#5C6B64]">{u.email || "—"}<div className="text-xs text-[#8A9A86]">{u.phone || ""}</div></td>
                  <td className="px-4 py-2.5 text-[#5C6B64]"><span className="inline-flex items-center gap-1"><FileCheck2 className="w-3.5 h-3.5" /> {u.agreements_count}</span></td>
                  <td className="px-4 py-2.5 text-right"><button data-testid={`admin-customer-manage-${u.id}`} onClick={() => navigate(`/admin/customers/${u.id}`)} className="btn-secondary text-xs py-1.5 px-3">Manage</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pagination page={page} total={total} pageSize={pageSize} onPage={(p) => load(q.trim(), p, status, range)} testid="admin-customers-pagination" />
    </div>
  );
}
